/**
 * OncallImport (ONCALL-IMPORT-01, Seth 2026-10-22: "along the paint toolbar, could I
 * have an upload option? Upload a PDF or picture or file and it would fill in the
 * calendar") — the Upload a Sheet door on the Paint bar.
 *
 * ONCALL-IMPORT-MULTI (Seth 2026-10-23: "I'm trying to do 3 months… so 3 different
 * screenshots. Upload won't take more than 1 file."): the door now takes SEVERAL
 * files — one PDF of the whole quarter, or a screenshot per month — reads them all
 * at once and merges the rows by date (a later file wins a date two files share).
 *
 *   1 · pick the files (photos, screenshots or PDFs of the on-call sheet)
 *   2 · the reader (the `oncall-import` function — same engine as the tag-photo
 *       reader) returns date · primary · backup for every day it can read
 *   3 · REVIEW: every row matched to the roster by name, unreadable names and
 *       dates outside the period flagged, days already painted shown as
 *       "replaces Camron" — the office fixes any row with the same selects the
 *       Paint bar uses, unticks what it doesn't want, then APPLIES.
 *       STACKED NAMES (ONCALL-IMPORT-TUNE): on the handwritten grid a day's two
 *       names sit one above the other across the cell's top line; the switch says
 *       which of the two is the primary and swaps every row, ⇅ swaps one row.
 *
 * Nothing lands on the calendar until Apply; Apply writes through the paint
 * tool's own upsert with source 'import'.
 */
import { useMemo, useRef, useState, type ReactNode } from "react";
import { Upload, FileText, Image as ImageIcon, Loader2, AlertTriangle, Check, X, ArrowUpDown, Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { fmtWeekday, fmtShort, type OncallPeriod, type OncallDay, type OncallDayInput } from "./oncall";

interface ReadDay { date: string; primary: string; backup: string; note: string; struck?: string }
interface Unsure { date: string; text: string; reason: string }
interface ReadResult { ok: boolean; days?: ReadDay[]; unsure?: Unsure[]; sheet_title?: string; notes?: string; error?: string; version?: number }
interface Tech { empno: string; name: string; full?: string }
/** Which of a day's two stacked names is the primary — sent to the reader, and the review's switch. */
type PairOrder = "upper_primary" | "lower_primary";

const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp,image/gif";
const MAX_FILES = 12;
const norm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, "");
const kindOf = (f: File) => f.type.replace("application/", "").replace("image/", "");

/** Edit distance — how many single-letter changes turn one word into the other. */
function lev(a: string, b: string): number {
  const prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let diag = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j];
      prev[j] = Math.min(prev[j] + 1, prev[j - 1] + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
      diag = tmp;
    }
  }
  return prev[b.length];
}

/**
 * A name as written on the sheet → a roster tech: exact first name, then a 3+ letter prefix,
 * then initials, then the full name, then (4+ letters) a near miss two letters off — the
 * reader spells handwriting as it sees it ("miks" for Miles, ONCALL-IMPORT-TUNE) — only
 * ever when exactly one tech fits.
 */
export function matchTech(raw: string, techs: Tech[]): Tech | null {
  const n = norm(raw);
  if (!n) return null;
  const firsts = techs.map((t) => ({ t, f: norm(t.name), full: norm(t.full ?? t.name) }));
  const exact = firsts.filter((x) => x.f === n);
  if (exact.length === 1) return exact[0].t;
  if (n.length >= 3) {
    const pre = firsts.filter((x) => x.f.startsWith(n) || n.startsWith(x.f));
    if (pre.length === 1) return pre[0].t;
  }
  if (n.length <= 3) {
    const ini = firsts.filter((x) => x.full.split(" ").length > 0 && (x.full[0] + (x.full.split(" ")[1]?.[0] ?? "")).startsWith(n));
    if (ini.length === 1) return ini[0].t;
  }
  const full = firsts.filter((x) => x.full.replace(/\s+/g, "") === n.replace(/\s+/g, "") || x.full.includes(n));
  if (full.length === 1) return full[0].t;
  if (n.length >= 4) {
    const near = firsts.filter((x) => lev(n, x.f) <= 2);
    if (near.length === 1) return near[0].t;
  }
  return null;
}

async function blobToBase64(f: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? "").split(",")[1] ?? "");
    r.onerror = () => reject(r.error ?? new Error("could not read the file"));
    r.readAsDataURL(f);
  });
}

/**
 * What actually goes to the reader. A phone photo of the sheet is 4–12 MB; sent as
 * base64 inside JSON that is more than the function gateway accepts. Images are
 * redrawn at ≤ 2000 px on the long side as JPEG (a printed sheet reads perfectly at
 * that size — a few hundred KB); EXIF rotation is applied so a sideways photo arrives
 * upright. PDFs go as they are, capped at 4 MB.
 */
const MAX_EDGE = 2000;
async function prepareFile(f: File): Promise<{ base64: string; mime: string; bytes: number; note: string }> {
  if (f.type.startsWith("image/")) {
    const bmp = await createImageBitmap(f, { imageOrientation: "from-image" }).catch(() => null);
    if (bmp) {
      const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
      const w = Math.max(1, Math.round(bmp.width * scale));
      const h = Math.max(1, Math.round(bmp.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h);
        ctx.drawImage(bmp, 0, 0, w, h);
        bmp.close();
        const blob = await new Promise<Blob | null>((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.85));
        if (blob) return { base64: await blobToBase64(blob), mime: "image/jpeg", bytes: blob.size, note: `sent as a ${w}×${h} JPEG · ${(blob.size / 1024).toFixed(0)} KB` };
      }
    }
  }
  if (f.size > 4 * 1024 * 1024) throw new Error(f.type === "application/pdf" ? "that PDF is over 4 MB — export it smaller, or screenshot each page" : "that image couldn't be shrunk and is too large — a screenshot of it works");
  return { base64: await blobToBase64(f), mime: f.type, bytes: f.size, note: `sent as ${kindOf(f)} · ${(f.size / 1024).toFixed(0)} KB` };
}

/**
 * What supabase-js hands back when the function call fails, in office words. A non-2xx
 * answer carries the function's own JSON error in `context` (a Response); the timeout
 * fuse in lib/supabase (FN-TIMEOUT-01) aborts with a TimeoutError; a dead connection
 * is "Failed to send a request to the Edge Function".
 */
async function readerError(error: unknown, sentAs: string): Promise<string> {
  const ctx = (error as { context?: unknown })?.context;
  if (typeof Response !== "undefined" && ctx instanceof Response) {
    try { const j = await ctx.clone().json(); if (j?.error) return String(j.error); } catch { /* not JSON */ }
    return `the reader answered ${ctx.status}`;
  }
  const name = (ctx as { name?: string } | undefined)?.name;
  if (name === "TimeoutError" || name === "AbortError") return "the reader took longer than it is allowed (about 3 minutes) — try that page on its own";
  const msg = (error as Error)?.message ?? String(error);
  if (/Failed to send a request/i.test(msg)) return `it never reached the reader (${sentAs}) — check the connection and try again`;
  return msg;
}

interface Picked {
  id: string;
  file: File;
  status: "pending" | "reading" | "done" | "error";
  sentAs?: string;
  days?: ReadDay[];
  unsure?: Unsure[];
  title?: string;
  notes?: string;
  error?: string;
  /** The stacked-name order the reader used for this file — rows are flipped when the switch differs. */
  readWith?: PairOrder;
}

interface ReviewRow {
  date: string;
  primaryRaw: string;
  backupRaw: string;
  note: string;
  primary: string;   // empno or ""
  backup: string;    // empno or ""
  include: boolean;
  inPeriod: boolean;
  /** Which file the row came from (only when more than one file was read). */
  from: string;
  /** Another file read this date differently — the later file won. */
  also: string;
  /** Names crossed out on the sheet for this day (shown, never applied). */
  struck: string;
}

const fileId = (f: File) => `${f.name}:${f.size}:${f.lastModified}`;

/** Merge every read file into one row per date — a later file wins a date two files share. */
function buildRows(picked: Picked[], pairOrder: PairOrder, techs: Tech[], period: OncallPeriod): ReviewRow[] {
  const done = picked.filter((p) => p.status === "done");
  const byDate = new Map<string, { d: ReadDay; from: string; also: string[] }>();
  for (const p of done) {
    for (const raw of p.days ?? []) {
      const d: ReadDay = p.readWith === pairOrder || !raw.backup ? raw : { ...raw, primary: raw.backup, backup: raw.primary };
      const prev = byDate.get(d.date);
      const also = prev ? [...prev.also] : [];
      if (prev && (prev.d.primary !== d.primary || prev.d.backup !== d.backup)) also.push(`${prev.d.primary}${prev.d.backup ? ` / ${prev.d.backup}` : ""} on ${prev.from}`);
      byDate.set(d.date, { d, from: p.file.name, also });
    }
  }
  return [...byDate.values()].sort((a, b) => a.d.date.localeCompare(b.d.date)).map(({ d, from, also }) => {
    const p = matchTech(d.primary, techs);
    const b = d.backup ? matchTech(d.backup, techs) : null;
    const inPeriod = d.date >= period.starts_on && d.date <= period.ends_on;
    return { date: d.date, primaryRaw: d.primary, backupRaw: d.backup, note: d.note, primary: p?.empno ?? "", backup: b && b.empno !== p?.empno ? b.empno : "", include: !!p && inPeriod, inPeriod, from: done.length > 1 ? from : "", also: also.join("; "), struck: d.struck ?? "" };
  });
}

/** Primary ⇄ backup on one row. A row the office unticked stays unticked unless it had no primary before. */
function swapRow(r: ReviewRow): ReviewRow {
  if (!r.backupRaw && !r.backup) return r;
  const primary = r.backup;
  return { ...r, primaryRaw: r.backupRaw, backupRaw: r.primaryRaw, primary, backup: r.primary, include: r.inPeriod && !!primary && (r.include || !r.primary) };
}

function SegBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={cn("rounded-md px-2.5 py-1 text-[12px] font-semibold transition-colors", active ? "bg-white text-slate-900 shadow-sm ring-1 ring-black/5" : "text-slate-500 hover:text-slate-800")}>
      {children}
    </button>
  );
}

export function ImportSheetModal({ period, techs, existing, onClose, onApply, saving }: {
  period: OncallPeriod;
  techs: Tech[];
  existing: Map<string, OncallDay>;
  onClose: () => void;
  onApply: (rows: OncallDayInput[]) => void;
  saving: boolean;
}) {
  const [picked, setPicked] = useState<Picked[]>([]);
  const [hint, setHint] = useState("");
  const [pairOrder, setPairOrder] = useState<PairOrder>("upper_primary");
  const [reading, setReading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rows, setRows] = useState<ReviewRow[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const nameOf = (e: string) => techs.find((t) => t.empno === e)?.name ?? e;

  /** Add files from the picker or a drop — skips duplicates, refuses what the reader can't take. */
  const add = (list: FileList | File[] | null) => {
    const files = Array.from(list ?? []);
    if (!files.length) return;
    setErr(null); setRows(null);
    const problems: string[] = [];
    setPicked((prev) => {
      const next = [...prev];
      for (const f of files) {
        if (next.some((p) => p.id === fileId(f))) continue;
        if (!ACCEPT.split(",").includes(f.type)) {
          problems.push(f.type === "image/heic" || /\.heic$/i.test(f.name)
            ? `${f.name}: iPhone HEIC photos can't be read — screenshot it, or export it as a JPG or PDF`
            : `${f.name}: that file type (${f.type || f.name.split(".").pop()}) can't be read — a PDF, JPG, PNG or WebP works`);
          continue;
        }
        if (f.size > 20 * 1024 * 1024) { problems.push(`${f.name}: over 20 MB — a screenshot or a smaller export works`); continue; }
        if (next.length >= MAX_FILES) { problems.push(`${f.name}: ${MAX_FILES} files at a time`); continue; }
        next.push({ id: fileId(f), file: f, status: "pending" });
      }
      return next;
    });
    if (problems.length) setErr(problems.join(" · "));
    if (inputRef.current) inputRef.current.value = "";
  };
  const remove = (id: string) => { setPicked((ps) => ps.filter((p) => p.id !== id)); setRows(null); setErr(null); };

  /** Read every file that hasn't been read yet — all at once, each its own call — then merge. */
  const read = async () => {
    const todo = picked.filter((p) => p.status !== "done");
    const order = pairOrder;
    if (!todo.length) { if (picked.length) setRows(buildRows(picked, order, techs, period)); return; }
    setReading(true); setErr(null);
    setPicked((ps) => ps.map((p) => (p.status === "done" ? p : { ...p, status: "reading", error: undefined })));
    const roster = techs.map((t) => ({ empno: t.empno, name: t.full ?? t.name }));
    const outcomes = await Promise.all(todo.map(async (p): Promise<Picked> => {
      let out: Picked;
      try {
        const prepared = await prepareFile(p.file);
        const { data, error } = await supabase.functions.invoke("oncall-import", {
          body: { file_base64: prepared.base64, mime: prepared.mime, period_start: period.starts_on, period_end: period.ends_on, roster, hint: hint.trim() || null, pair_order: order },
        });
        if (error) throw new Error(await readerError(error, prepared.note));
        const res = data as ReadResult;
        if (!res?.ok) throw new Error(res?.error ?? "the reader returned nothing");
        out = { ...p, status: "done", sentAs: prepared.note, days: res.days ?? [], unsure: res.unsure ?? [], title: res.sheet_title || undefined, notes: res.notes || undefined, error: undefined, readWith: order };
      } catch (e) {
        out = { ...p, status: "error", error: (e as Error)?.message ?? "try again" };
      }
      setPicked((ps) => ps.map((x) => (x.id === out.id ? out : x)));
      return out;
    }));
    const merged = picked.map((p) => outcomes.find((o) => o.id === p.id) ?? p);
    setReading(false);
    // Nothing read at all: stay on the file list — each file row shows what went wrong.
    if (merged.some((p) => p.status === "done")) setRows(buildRows(merged, order, techs, period));
  };

  const flip = (o: PairOrder) => {
    if (o === pairOrder) return;
    setPairOrder(o);
    setRows((rs) => (rs ? rs.map(swapRow) : rs));
  };

  const doneFiles = picked.filter((p) => p.status === "done");
  const failed = picked.filter((p) => p.status === "error");
  const pending = picked.filter((p) => p.status === "pending" || p.status === "reading");
  const titles = doneFiles.map((p) => p.title).filter((t): t is string => !!t);
  const unsure = doneFiles.flatMap((p) => (p.unsure ?? []).map((u) => ({ ...u, from: doneFiles.length > 1 ? p.file.name : "" })));
  const notes = doneFiles.map((p) => p.notes).filter(Boolean).join(" · ");

  const stats = useMemo(() => {
    if (!rows) return null;
    const matched = rows.filter((r) => r.primary).length;
    const outside = rows.filter((r) => !r.inPeriod).length;
    const replaces = rows.filter((r) => r.include && existing.has(r.date)).length;
    const included = rows.filter((r) => r.include && r.primary && r.inPeriod).length;
    const twice = rows.filter((r) => r.also).length;
    return { total: rows.length, matched, outside, replaces, included, twice };
  }, [rows, existing]);

  const setRow = (i: number, patch: Partial<ReviewRow>) => setRows((rs) => rs ? rs.map((r, k) => (k === i ? { ...r, ...patch } : r)) : rs);
  const apply = () => {
    if (!rows) return;
    const out: OncallDayInput[] = rows
      .filter((r) => r.include && r.primary && r.inPeriod)
      .map((r) => ({ day: r.date, primary_empno: r.primary, backup_empno: r.backup && r.backup !== r.primary ? r.backup : null, note: r.note || existing.get(r.date)?.note || null, source: "import" as const }));
    if (out.length) onApply(out);
  };

  const sel = "h-7 rounded-md border border-hairline bg-white px-1.5 text-[12.5px] font-semibold text-slate-800 focus:border-brand-500 focus:outline-none";
  const fileIcon = (f: File, cls = "h-4 w-4") => (f.type === "application/pdf" ? <FileText className={cn(cls, "text-brand-600")} /> : <ImageIcon className={cn(cls, "text-brand-600")} />);
  const readLabel = reading
    ? picked.length > 1 ? `Reading ${pending.length} of ${picked.length}…` : "Reading the sheet…"
    : pending.length === 0 && doneFiles.length ? "Show the rows"
    : picked.length > 1 ? `Read ${pending.length} file${pending.length === 1 ? "" : "s"}` : "Read the sheet";

  return (
    <Modal open onClose={onClose} size={rows ? "xl" : "md"}
      title={<span className="flex items-center gap-2">Upload a Sheet<InfoTip label="How the upload works">Pick the on-call sheet as one PDF or as a screenshot per month — several files at once is fine. Pegasus reads them into one row per day; you check the rows against the roster, untick anything wrong, then Apply. Nothing lands on the calendar before you Apply.</InfoTip></span>}
      subtitle={`${period.label ?? `${fmtShort(period.starts_on)} – ${fmtShort(period.ends_on)}`} · one PDF or a screenshot per month · days outside the period are read but not applied`}>
      {!rows ? (
        <div className="flex flex-col gap-3">
          <input ref={inputRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => add(e.target.files)} />
          {picked.length === 0 ? (
            <button type="button" onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
              className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 px-4 py-8 text-center transition-colors hover:border-brand-300 hover:bg-slate-50">
              <Upload className="h-7 w-7 text-slate-400" />
              <span className="text-sm font-bold text-slate-900">Drop the sheet here, or click to pick it</span>
              <span className="text-xs text-slate-500">A PDF of the whole period, or a photo or screenshot of each month — pick several at once</span>
            </button>
          ) : (
            <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }} className="overflow-hidden rounded-xl border border-hairline">
              <ul className="divide-y divide-slate-100">
                {picked.map((p) => (
                  <li key={p.id} className="flex items-center gap-2.5 px-3 py-2">
                    {fileIcon(p.file)}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-semibold text-slate-900">{p.file.name}</div>
                      <div className={cn("text-[11.5px]", p.status === "error" ? "break-words text-rose-700" : "truncate text-slate-500")}>
                        {p.status === "pending" && `${(p.file.size / 1024).toFixed(0)} KB · ${kindOf(p.file)}`}
                        {p.status === "reading" && "Reading…"}
                        {p.status === "done" && `${p.days?.length ?? 0} day${(p.days?.length ?? 0) === 1 ? "" : "s"}${p.title ? ` · ${p.title}` : ""}${p.sentAs ? ` · ${p.sentAs}` : ""}`}
                        {p.status === "error" && p.error}
                      </div>
                    </div>
                    {p.status === "reading" && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand-600" />}
                    {p.status === "done" && <Check className="h-4 w-4 shrink-0 text-emerald-600" />}
                    {p.status === "error" && <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />}
                    <button type="button" onClick={() => remove(p.id)} disabled={reading} title="Remove" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"><X className="h-3.5 w-3.5" /></button>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => inputRef.current?.click()} disabled={reading || picked.length >= MAX_FILES}
                className="flex w-full items-center justify-center gap-1.5 border-t border-dashed border-slate-200 bg-slate-50/60 px-3 py-2 text-[12.5px] font-semibold text-brand-700 hover:bg-brand-50/60 disabled:opacity-40">
                <Plus className="h-3.5 w-3.5" /> Add another file
              </button>
            </div>
          )}
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Anything the reader should know <span className="font-medium normal-case tracking-normal text-slate-400">· optional</span></label>
            <input value={hint} onChange={(e) => setHint(e.target.value)} disabled={reading} placeholder="e.g. 'B =' means backup · the right column is the backup · 'Cam' is Camron" className="w-full rounded-lg border border-hairline px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
          </div>
          {err && <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-800"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{err}</div>}
          <div className="flex items-center justify-end gap-2 pt-1">
            {reading && <span className="mr-auto text-[11.5px] text-slate-400">About a minute a page</span>}
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={!picked.length || reading} onClick={() => void read()}>
              {reading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {readLabel}</> : readLabel}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-slate-600">
            <span className="font-bold text-slate-900">Read {stats?.total} day{stats?.total === 1 ? "" : "s"}</span>
            {doneFiles.length > 1 && <span className="text-slate-400">· {doneFiles.length} files</span>}
            {titles.length > 0 && <span className="text-slate-400">· {titles.join(" · ")}</span>}
            {doneFiles.length === 1 && doneFiles[0].sentAs && <span className="text-slate-400">· {doneFiles[0].sentAs}</span>}
            <span>· {stats?.matched} matched to the roster</span>
            {!!stats?.outside && <span className="text-amber-700">· {stats.outside} outside the period (not applied)</span>}
            {!!stats?.replaces && <span className="text-amber-700">· {stats.replaces} replace a day already set</span>}
            {!!stats?.twice && <span className="text-amber-700">· {stats.twice} read differently in two files</span>}
            <button type="button" onClick={() => setRows(null)} className="ml-auto text-[12px] font-semibold text-brand-700 hover:underline">Change files</button>
          </div>
          {failed.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div>{failed.map((p) => <div key={p.id}><span className="font-semibold">{p.file.name}</span> — {p.error}</div>)}</div>
            </div>
          )}
          <div className="flex items-center gap-2 rounded-xl border border-hairline bg-slate-50/60 px-3 py-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Stacked names</span>
            <InfoTip label="Which stacked name is the primary">On the handwritten grid a day's two names sit one above the other across the cell's top line. Pick which one is the primary on call — the other is the backup. Switching this swaps every row below; ⇅ swaps one row.</InfoTip>
            <div className="ml-auto flex rounded-lg border border-hairline bg-slate-100/80 p-0.5">
              <SegBtn active={pairOrder === "upper_primary"} onClick={() => flip("upper_primary")}>Upper Is Primary</SegBtn>
              <SegBtn active={pairOrder === "lower_primary"} onClick={() => flip("lower_primary")}>Lower Is Primary</SegBtn>
            </div>
          </div>
          <div className="max-h-[52vh] overflow-y-auto rounded-xl border border-hairline">
            <table className="w-full text-[12.5px]">
              <thead className="sticky top-0 bg-slate-50 text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-slate-500">
                <tr><th className="px-3 py-2 text-left">Apply</th><th className="px-2 py-2 text-left">Date</th><th className="px-2 py-2 text-left">Primary</th><th className="px-1 py-2" /><th className="px-2 py-2 text-left">Backup</th><th className="px-2 py-2 text-left">Now</th><th className="px-2 py-2 text-left">On the sheet</th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const cur = existing.get(r.date);
                  const problem = !r.inPeriod ? "outside the period" : !r.primary ? `couldn't match “${r.primaryRaw}”` : r.backupRaw && !r.backup ? `backup “${r.backupRaw}” not matched` : null;
                  const changes = !!cur && r.include && (cur.primary_empno !== r.primary || (cur.backup_empno ?? "") !== r.backup);
                  return (
                    <tr key={r.date} className={cn("border-t border-slate-100", !r.inPeriod && "opacity-50", problem && r.inPeriod && "bg-amber-50/50")}>
                      <td className="px-3 py-1.5"><input type="checkbox" checked={r.include} disabled={!r.inPeriod || !r.primary} onChange={(e) => setRow(i, { include: e.target.checked })} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600" /></td>
                      <td className="whitespace-nowrap px-2 py-1.5 font-semibold text-slate-800">{fmtWeekday(r.date)}</td>
                      <td className="px-2 py-1.5">
                        <select value={r.primary} onChange={(e) => setRow(i, { primary: e.target.value, include: !!e.target.value && r.inPeriod, backup: r.backup === e.target.value ? "" : r.backup })} className={cn(sel, !r.primary && "border-amber-300 bg-amber-50")}>
                          <option value="">{r.primaryRaw ? `“${r.primaryRaw}” — pick` : "Pick…"}</option>
                          {techs.map((t) => <option key={t.empno} value={t.empno}>{t.name}</option>)}
                        </select>
                      </td>
                      <td className="px-1 py-1.5">
                        <button type="button" title="Swap primary and backup" disabled={!r.backup && !r.backupRaw} onClick={() => setRows((rs) => rs ? rs.map((x, k) => (k === i ? swapRow(x) : x)) : rs)}
                          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-25 disabled:hover:bg-transparent">
                          <ArrowUpDown className="h-3.5 w-3.5" />
                        </button>
                      </td>
                      <td className="px-2 py-1.5">
                        <select value={r.backup} onChange={(e) => setRow(i, { backup: e.target.value })} className={cn(sel, r.backupRaw && !r.backup && "border-amber-300 bg-amber-50")}>
                          <option value="">{r.backupRaw ? `“${r.backupRaw}” — pick` : "No backup"}</option>
                          {techs.filter((t) => t.empno !== r.primary).map((t) => <option key={t.empno} value={t.empno}>{t.name}</option>)}
                        </select>
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-slate-500">{cur ? <span className={cn(changes && "text-amber-700")}>{changes ? "replaces " : ""}{nameOf(cur.primary_empno)}{cur.backup_empno ? ` · ${nameOf(cur.backup_empno)}` : ""}</span> : <span className="text-slate-300">empty</span>}</td>
                      <td className="px-2 py-1.5 text-[11.5px] text-slate-500">
                        {problem ? <span className="text-amber-700">{problem}</span> : <span>{r.primaryRaw}{r.backupRaw ? ` · ${r.backupRaw}` : ""}</span>}
                        {r.note ? <span className="text-slate-400"> · {r.note}</span> : null}
                        {r.struck ? <span className="text-slate-400"> · <s>{r.struck}</s> crossed out</span> : null}
                        {r.also ? <span className="text-amber-700"> · also {r.also}</span> : null}
                        {r.from ? <span className="text-slate-300"> · {r.from}</span> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {unsure.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
              <div className="font-bold">Couldn't read with confidence — set these by hand</div>
              <ul className="mt-1 list-disc pl-4">{unsure.slice(0, 12).map((u, k) => <li key={k}>{u.date ? `${u.date} · ` : ""}{u.text}{u.reason ? ` — ${u.reason}` : ""}{u.from ? <span className="text-amber-700/70"> · {u.from}</span> : null}</li>)}</ul>
            </div>
          )}
          {notes && <div className="text-[11.5px] text-slate-400">Reader's note: {notes}</div>}
          {err && <div className="text-[12px] font-medium text-rose-700">{err}</div>}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={!stats?.included || saving} onClick={apply}>
              {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Applying…</> : <><Check className="h-3.5 w-3.5" /> Apply {stats?.included} day{stats?.included === 1 ? "" : "s"}</>}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
